import { axiosApi } from '@/services/http/axiosApi'
import {
  API_GET_COMPANY_BY_ID,
  API_GET_COMPANY_SETTINGS,
  API_PATCH_COMPANY_BY_ID
} from '@/constants/urls'
import type { AxiosResponse } from 'axios'
import type {
  COMPANY_ACCOUNT_TYPE,
  COMPANY_ADDRESS_USAGE,
  COMPANY_CATEGORY,
  COMPANY_NUMBER_OF_EMPLOYEES,
  COMPANY_TYPE
} from '@/constants/company'

export interface CompanyType {
  id: string
  name: string
  displayName: string
  recordType: string
  addresses: Address[]
  status?: string
  accountManagementType?: string
  businessLine: string
  businessStatus: string
  businessType: string
  classificationLabel: string
  closingReason: string
  mainActivity?: string
  mainDivision: string
  nationalId?: string
  noContracting: boolean
  accountType: COMPANY_ACCOUNT_TYPE
  comments?: string
  isAutoGeneration?: boolean
  isGlobalMaster?: boolean
  globalMasterCompanyId: string
  isSubsidiary?: boolean
  businessCreationYear?: number
  companyStatus?: string
  leadSource?: string
  category?: COMPANY_CATEGORY
  type?: COMPANY_TYPE
  rsuId: string
  numberOfEmployees: COMPANY_NUMBER_OF_EMPLOYEES
  domains: string[]
  entities?: string[]
  isCompanyLoyaltyMember?: boolean
  loyalty?: Loyalty
  tracking?: Tracking
}

export interface Address {
  address1: string
  address2?: string
  address3?: string
  poBox?: string
  city: string
  stateCode?: string
  zipCode: string
  countryCode: string
  usageType?: COMPANY_ADDRESS_USAGE
}

export interface Loyalty {
  id?: string
  programId?: string
  programName: string
  programStatus: string
  invitationLink?: string
}

export interface Tracking {
  createdDate?: string
  creationSystem?: string
  createdBy?: string
  updatedDate?: string
  updatedSystem?: string
  updatedBy?: string
}

export enum OPERATION_CLASS_ENUM {
  EARN = 'EARN',
  BURN = 'BURN'
}

export enum OPERATION_TYPE_ENUM {
  CREDIT = 1,
  DEBIT = 0
}

export enum CAMPAIGN_CODE_ENUM {
  ADJUSTMENT = 'Adjustment',
  BONUS = 'Bonus',
  EVENT = 'Event',
  OFFER = 'Offer',
  STAY = 'Stay',
  REDEMPTION = 'Redemption'
}

export interface LoyaltyCompanyOperation {
  companyOperationId: number
  operationClass: OPERATION_CLASS_ENUM
  credit: OPERATION_TYPE_ENUM
  points: number
  creationDate: Date
  animation: {
    code: string
    name: string
    type: string
  }
  campaign: {
    code: CAMPAIGN_CODE_ENUM
    name: string
  }
  employeeOperationId: number
  employee: {
    pmid: string
    firstName: string
    lastName: string
  }
}

export type CompanySettingsType = Record<string, unknown>

export const getCompanyById = async (companyId: string): Promise<AxiosResponse<CompanyType>> =>
  await axiosApi.get(`${API_GET_COMPANY_BY_ID(companyId)}`)

export const patchCompany = async (
  companyId: string,
  company: Partial<CompanyType>
): Promise<AxiosResponse<CompanyType>> =>
  await axiosApi.patch(`${API_PATCH_COMPANY_BY_ID(companyId)}`, company)

export const getCompanySettings = async (
  companyId: string
): Promise<AxiosResponse<CompanySettingsType>> =>
  await axiosApi.get(`${API_GET_COMPANY_SETTINGS(companyId)}`)

export const updateCompanySettings = async (
  companyId: string,
  companySettings: CompanySettingsType
): Promise<AxiosResponse<void>> =>
  await axiosApi.put(`${API_GET_COMPANY_SETTINGS(companyId)}`, companySettings)
